/**
 * THE FEATURE MANIFEST CONTRACT
 * =============================
 *
 * A chapter agent adds `src/features/<slug>/manifest.ts` and edits NO shared file.
 * Routes, navigation, export handlers, drill declarations and locale namespaces are
 * all discovered from the manifest at build time by `src/features/registry.ts`.
 *
 * There is no central route table to merge, no `routes.ts` to append to, and no
 * `exports.ts` to register in. That is the whole design goal: eight agents working
 * in parallel must not collide on one file, and a merge conflict in a shared route
 * list is the single most likely way this repository would stop building.
 *
 * WHAT THE TYPES ENFORCE, so a census cannot be failed by accident:
 *
 *   G2 test d (export census) — a route whose `surfaces` include a table, grid or
 *     chart is a `DataRoute`, and `DataRoute.exports` is REQUIRED. A data route with
 *     no export handler does not type-check. The runtime census in
 *     tools/export-census.mjs is the second line, for the `as never` case.
 *
 *   G2 test f (drill census) — every `DataRoute` must declare `drill`, either a
 *     target or an explicit `{ terminal: true, reason }`. "Zero terminal aggregates
 *     without a documented reason" is a type requirement, not a review comment.
 *
 *   G6 — every human-readable field is a catalogue KEY, never a string. There is no
 *     `title: string` anywhere in this file, only `titleKey`.
 *
 *   design/09 §12.10 — a route declaring a `chart` surface must declare
 *     `tableViewReachable: true`; there is no other permitted value.
 */

import type { ComponentType } from 'react';
import type { CapabilityAction, CapabilityKind } from '../auth/capabilities';
import type { ExportFormat, ExportRequest } from '../export/types';
import type { GuideTable } from './guides';

// ── namespace ownership ──────────────────────────────────────────────────────

/**
 * The seven chapter namespaces. A feature directory name must be one of these, and
 * an agent owns exactly one. See CLAUDE.md for the full ownership table including
 * the ADR number blocks.
 *
 * ch07 `conformance` and ch08 `programme` were RETIRED on 2026-09-17 (ADR 0081). They were
 * fixtures end to end with no data model anywhere in the estate, they were withdrawn from
 * navigation first, and the code was then deleted. Their blocks are not reassigned to a
 * chapter — they became the shared-layer block, which is why `ADR_BLOCKS` below has an owner
 * that is not a chapter.
 */
export const FEATURE_SLUGS = [
  'foundations', // ch00
  'strategic', // ch01
  'preseason', // ch02
  'launch', // ch03
  'inseason', // ch04
  'exit', // ch05
  'postseason', // ch06
] as const;

export type FeatureSlug = (typeof FEATURE_SLUGS)[number];

/**
 * The ADR number domain each chapter owns, LIVE numbers only. `bata-reqs` ADR 1000
 * (2026-09-22) widened every domain to 1000 numbers, one domain per first digit,
 * binding all five repositories. Numbers below 1000 are frozen history — never
 * renumbered, never reallocated — and are not represented here; see
 * `LEGACY_ADR_BLOCKS` in `tools/feature-census.mjs` for that table, kept only so an
 * existing pre-2026-09-22 ADR's chapter ownership can still be checked.
 *
 * `shared` is not a chapter and has no feature directory. It holds decisions about
 * `src/primitives/`, `src/planning/`, `src/charts/`, `src/api/`, `tools/` and this file
 * — the modules every chapter consumes and none owns — and under ADR 1000 it also
 * folds what were ch07 and ch08's domains, promoting the fold ADR 0081 did locally
 * (0080–0099) to programme level.
 *
 * The scheme this replaced ran out twice in one afternoon: the old `shared` block
 * (0080–0099) filled five days after ADR 0081 opened it, and the same day a second
 * decision found ch00's own block full too, with the `01NN`-extension mechanism
 * already spent on the same block once before. 1000 numbers per domain is sized
 * against that measured rate, not against a fresh guess.
 */
export const ADR_BLOCKS: Record<FeatureSlug | 'bootstrap' | 'shared', readonly [number, number]> = {
  bootstrap: [1000, 1999],
  foundations: [2000, 2999],
  strategic: [3000, 3999],
  preseason: [4000, 4999],
  launch: [5000, 5999],
  inseason: [6000, 6999],
  exit: [7000, 7999],
  postseason: [8000, 8999],
  shared: [9000, 9999],
};

// ── surfaces ─────────────────────────────────────────────────────────────────

/**
 * A surface that shows numbers. G2.4: "There is no screen from which a planner
 * cannot get the numbers out." Declaring one of these obliges an export handler.
 */
export const DATA_SURFACES = ['table', 'grid', 'chart', 'tile'] as const;
export type DataSurface = (typeof DATA_SURFACES)[number];

/** A surface that shows no aggregate: a form, a wizard step, a settings page. */
export const NON_DATA_SURFACES = ['form', 'wizard', 'detail', 'settings'] as const;
export type NonDataSurface = (typeof NON_DATA_SURFACES)[number];

export type Surface = DataSurface | NonDataSurface;

// ── export registration (G2.4, G2 test d) ────────────────────────────────────

/**
 * The screen state an export is taken FROM.
 *
 * G2.4's fidelity requirement is that an export reflects the view the planner is looking
 * at — its filters, its sort, its column set, its drill level — and not the unfiltered
 * underlying query. `resolve` used to take no arguments, so a resolver had no way to see
 * any of that, and every chapter that wired one worked around it the same way: by reading
 * `window.location.search` inside the resolver itself. That works because G2.2 makes the
 * URL the state — `?runId=`, `?season=`, `?option=` — but it means the resolver reaches
 * for a global, which cannot be tested without a `jsdom` navigation and cannot be called
 * at all from a worker or a server render.
 *
 * So the scope is PASSED, and it is passed as what the chapters were already reading: the
 * URL. The shape is deliberately not "the query state", because there is no type that
 * could describe eight chapters' query state without becoming a union of all of them.
 *
 * Both `search` and `params` are carried. `search` is the raw string, because a chapter's
 * own scope parser already takes one (`readScope(search)` in `src/features/inseason/`);
 * `params` is the parsed form, so the common case does not re-parse.
 */
export interface ExportScope {
  /** The absolute route path the export was requested from, e.g. `/inseason/workbench`. */
  readonly path: string;
  /** The URL search string, leading `?` included, or `''`. */
  readonly search: string;
  /** `search`, parsed. A repeated parameter keeps its LAST value, as `URLSearchParams.get` does. */
  readonly params: Readonly<Record<string, string>>;
}

/**
 * Produces the export payload for the view as it currently stands.
 *
 * A resolver that does not need the scope may still declare no parameters — a zero-arity
 * function is assignable here — so widening the contract broke no existing registration.
 */
export type ExportResolver = (scope: ExportScope) => Promise<ExportRequest>;

/**
 * How a route's numbers leave the product. `resolve` returns the CURRENT view — the
 * filters, sort, column set and drill level the planner is looking at — because
 * G2.4's fidelity requirement is that the export reflects that view and not the
 * unfiltered underlying query.
 */
export interface ExportRegistration {
  /** Stable id, unique across the product. Convention: `<slug>.<route>.<surface>`. */
  readonly id: string;
  /**
   * Which formats this surface offers: CSV and Excel, both, always — ADR 9010 as amended on
   * 2026-10-01 (DEC-98, the client's request), which realigns with G2.4. Typed to exactly
   * that pair, so a manifest that drops one or offers an image does not compile; the runtime
   * census catches the cast that defeats this.
   */
  readonly formats: typeof MANDATORY_EXPORT_FORMATS;
  /**
   * Produces the export payload for the view as it currently stands. Called by
   * src/export/, which owns provenance, numeric integrity, async and audit, and which
   * hands the resolver the scope the export was requested from.
   */
  readonly resolve: ExportResolver;
}

export const MANDATORY_EXPORT_FORMATS = ['csv', 'xlsx'] as const;
/** Re-exported from src/export/ so there is one definition, not two (G11). */
export type { ExportFormat };

// ── drill declaration (G2.2, G2 test f) ──────────────────────────────────────

export type DrillDeclaration =
  | {
    /** The route path one level down. G2.2: one click per level, breadcrumbed,
     *  reversible by the browser back button. */
    readonly to: string;
    readonly grain: DrillGrain;
    /** Cross-drill targets — article → stores, sizes, cluster peers, buy line,
     *  allocations. G2.2: "Cross-drill, not just down". */
    readonly alsoTo?: readonly string[];
  }
  | {
    readonly terminal: true;
    /** G2 test f: "zero terminal aggregates without a documented reason". */
    readonly reason: string;
  };

export const DRILL_GRAINS = [
  'division', 'category', 'subcategory', 'article', 'sku', 'size',
  'country', 'cluster', 'store', 'door', 'week', 'day', 'transaction',
] as const;
export type DrillGrain = (typeof DRILL_GRAINS)[number];

// ── routes ───────────────────────────────────────────────────────────────────

interface RouteBase {
  /** Route path, relative to the feature root. `''` is the feature index. */
  readonly path: string;
  /** ICU catalogue key for the page title. G6 — never a literal. */
  readonly titleKey: string;
  /**
   * ICU key for the screen's FULL explanation, rendered in the Documentation screen under its
   * chapter — bata-ui#132 step 6. The screen itself keeps only a short `aboutKey` behind its
   * panels' (i) — there is no explanatory line under the title (ADR 9031); the paragraphs a planner reads once go here, in one
   * place, instead of sitting inline on every visit.
   *
   * Must be `<slug>.documentation.<name>` in the chapter's OWN catalogue (a chapter documents
   * itself without editing another's), or `foundations.documentation.<name>` in the
   * Documentation screen's catalogue — and must exist in all three locales. `tools/doc-sections.mjs` fails `census:features` otherwise, so a
   * section cannot be declared and quietly never written.
   */
  readonly docSectionKey?: string;
  /** Lazy component. Code-splitting per route is what keeps the §2.1 budgets real. */
  readonly component: () => Promise<{ default: ComponentType }>;
  /**
   * The realm roles this route requires. The signed-in person needs EVERY one of them
   * (`hasGrants` in `src/auth/grants.ts`). Without them, the router renders the designed
   * no-permission state (G2 §2.6) in place of the screen. G3.1 enforces authorisation in the
   * data-access layer, never in the UI, so this is for navigation and the no-permission state,
   * not for security.
   *
   * It was declared as "the (role, scope, action) triple" and nothing read it until ADR 9014.
   * The triple is the platform's authorisation-table model (`bata-reqs` 17 §P6), and those
   * tables do not exist yet, so an entry here is a realm role code, as the token carries it.
   * When the tables land, the triple form arrives as a new field, not as a reinterpretation
   * of this one.
   */
  readonly requires?: readonly string[];
  /**
   * The capability this route is FOR — `GET /me/capabilities`' (kind, act), ADR 9022 and 9037.
   * When the platform's answer denies it, the router renders the reason in the screen's place
   * and the rail and the palette do not offer the entry. While the answer is unknown (loading,
   * failed, or a platform that predates the read) the route renders and the platform judges the
   * read, as every capability-gated control does (ADR 9022 decision 3). A missing (kind, act) is
   * denied (`not_declared`) — the contract's rule, `src/auth/capabilities.ts`.
   *
   * The platform's grant, not a role name — the "triple form" `requires` above promised. Like
   * `requires` it is navigation and the no-permission state, not security (G3.1): the platform
   * authorises the read whatever this says.
   */
  readonly capability?: RouteCapability;
  /**
   * The URL parameters this screen reads — issue 350, `docs/adr/9054`. The header's scope chips
   * show these and no others; the address bar still carries every parameter across screens
   * (`src/app/chrome/rail-layout.ts`), so a drill keeps its context.
   *
   * Every key the screen's code names — a `…_PARAM` constant or a literal at a `URLSearchParams`
   * read, in what the screen reaches — and nothing else: `src/app/chrome/url-param-census.test.ts`
   * derives the list from the code and fails when this one differs. `[]` is a screen that reads
   * none, and shows no chip. Left out, the chrome shows every parameter, as before issue 350 —
   * never none, because a missing declaration must not hide a perimeter (G16); the census fails
   * on a route that leaves it out.
   *
   * Not declared here: a table's own sort and filter keys (`<table>.sort`, `<table>.f.<column>`,
   * the ranked bar's), which the table states itself in its filter line; `preview`, which the page
   * announces itself. A run's id is declared but never drawn as a chip (planner review 2026-09-25).
   */
  readonly urlParams?: readonly string[];
}

/** A route's (kind, act) — ADR 9037. */
export interface RouteCapability {
  readonly kind: CapabilityKind;
  readonly action: CapabilityAction;
}

/** A route that shows numbers. Export and drill are both required. */
export interface DataRoute extends RouteBase {
  readonly surfaces: readonly Surface[];
  /** G2 test d — required, and there is no way to omit it. */
  readonly exports: ExportRegistration;
  /** G2 test f — required, either a target or a documented terminal. */
  readonly drill: DrillDeclaration;
  /**
   * design/09 §12.10 — "Every chart has a reachable table view." Required, and
   * `true` is the only permitted value, when a chart surface is declared.
   */
  readonly tableViewReachable?: true;
}

/** A route that shows no aggregate. Declaring an export here is a type error. */
export interface NonDataRoute extends RouteBase {
  readonly surfaces: readonly NonDataSurface[];
  readonly exports?: never;
  readonly drill?: never;
  readonly tableViewReachable?: never;
}

export type FeatureRoute = DataRoute | NonDataRoute;

/**
 * `requires`, named so a shell route that no manifest declares states its requirement in the
 * same type and is read by the same `hasGrants`. The User Management screen is such a route
 * (ADR 9014).
 */
export type RouteRequires = NonNullable<FeatureRoute['requires']>;

export const isDataRoute = (r: FeatureRoute): r is DataRoute => 'exports' in r && r.exports !== undefined;

// ── navigation ───────────────────────────────────────────────────────────────

export interface NavEntry {
  readonly labelKey: string;
  readonly to: string;
  /** Lucide icon name (ISC, §10.1). Resolved by the shell, so a feature does not
   *  import an icon set directly. */
  readonly icon?: string;
  readonly order: number;
  readonly children?: readonly NavEntry[];
  /**
   * A single screen not yet offered to a planner, inside a chapter that otherwise
   * works. The shell renders it greyed and inert in the header's row two — visible,
   * so the planner can see what the chapter will hold, and unclickable.
   *
   * The chapter-level `FeatureManifest.disabled` is the same idea one level up; an
   * entry inside a disabled chapter needs no flag of its own. Like that flag this is
   * a NAVIGATION AFFORDANCE ONLY: the route stays mounted and a pasted URL still
   * renders.
   *
   * `true` or absent, never `false`.
   */
  readonly disabled?: true;
}

// ── the manifest ─────────────────────────────────────────────────────────────

/**
 * The icons a chapter may put in the rail — ADR 9004's amendments, audit v4 G-15, v5 N-06.
 *
 * A closed vocabulary of NAMES, not of chapters: G5 forbids an enumeration of chapters in
 * code, and this is not one — any chapter picks from it, and a new icon is a new name here
 * plus its component in `src/app/chrome/rail-icons.ts`, which a `Record` over this type
 * forces to be exhaustive. A name rather than a component so a manifest imports no icon set
 * (the shell resolves it) and a typo is a compile error rather than a blank square.
 *
 * The names are the brand guide's module icons (p.14), which the guide says the build
 * imports "by the names above": Lucide, 24px box, 2px stroke. All seven are here, including
 * the long-range module's, which no chapter in this repository ships yet.
 */
export const CHAPTER_ICONS = [
  'database', 'trending-up', 'clipboard-list', 'split', 'activity', 'percent', 'chart-line',
] as const;
export type ChapterIcon = (typeof CHAPTER_ICONS)[number];

/**
 * Where a chapter sits in the rail — bata-ui#132 step 1. Three labelled sections, in this
 * order, which is the order `RAIL_GROUPS` declares and the rail renders:
 *
 * - `active` — the modules a planner runs decisions in today (the in-season cycle, exit).
 * - `setup` — configuration and analysis around them: the foundations, the read-back of
 *   what happened, the measurement of impact.
 * - `future` — chapters whose screens are drawn but not offered yet.
 *
 * A PLACEMENT, not a state. `disabled` and `notOffered()` still decide whether a chapter
 * can be opened; a `future` chapter that ships keeps working the day it is released even
 * if nobody moves it, and a `setup` chapter that is withdrawn is inert wherever it sits.
 * Tying the two together would make moving a chapter in the rail a release decision.
 *
 * A closed vocabulary of SECTIONS, not of chapters — the same reasoning as `CHAPTER_ICONS`:
 * any chapter picks one, and G5's rule against enumerating chapters in code is untouched.
 */
export const RAIL_GROUPS = ['active', 'setup', 'future'] as const;
export type RailGroup = (typeof RAIL_GROUPS)[number];

/** The section a manifest that names none is placed in. */
export const DEFAULT_RAIL_GROUP: RailGroup = 'setup';

export interface FeatureManifest {
  /**
   * MUST equal the directory name. `tools/feature-census.mjs` fails the build on a
   * mismatch, because a slug that disagrees with its directory breaks the locale
   * namespace merge silently.
   */
  readonly slug: FeatureSlug;
  /** ICU key for the chapter's own name in breadcrumbs and page titles. */
  readonly titleKey: string;
  /**
   * ICU key for the chapter's name as it appears in the header's chapter row, where
   * eight-plus chapters share one line. Falls back to `titleKey` when a chapter's
   * full name is already short (e.g. "Programme"). Optional so a short-named chapter
   * need not carry a second, identical catalogue key.
   */
  readonly navLabelKey?: string;
  /**
   * The chapter's icon in the rail. Below the `lg` breakpoint the rail shows icons only
   * (`sidebar-width-collapsed`), so a chapter without one cannot be told apart there; the
   * shell falls back to a neutral mark rather than to another chapter's icon.
   */
  readonly icon?: ChapterIcon;
  /**
   * The rail section this chapter is listed under — see `RAIL_GROUPS`. Absent means
   * `DEFAULT_RAIL_GROUP`. It moves the chapter in the rail and changes nothing else: the
   * chapter the planner is IN still heads the rail whatever its section, and whether it
   * can be opened is still `disabled`'s call.
   */
  readonly railGroup?: RailGroup;
  /**
   * Routes are mounted under `/<slug>/<path>`. G2.2: "The URL is the state" — any
   * screen, filter set, drill path and scenario is a shareable, bookmarkable link.
   */
  readonly routes: readonly FeatureRoute[];
  readonly navigation?: readonly NavEntry[];
  /**
   * A chapter whose screens are not yet offered to a planner. The shell greys the
   * chapter in the header's row one and renders its screens in row two as inert
   * buttons: the planner can SEE what the chapter will contain and cannot click
   * into it. This is a NAVIGATION AFFORDANCE ONLY — the routes stay mounted and a
   * pasted URL still renders, so a deep link, a drill `alsoTo` target and an e2e
   * journey all behave exactly as they did before the flag was set.
   *
   * `true` or absent, never `false`: a chapter is disabled by saying so, and the
   * default is a working chapter.
   */
  readonly disabled?: true;
  /**
   * The locale namespace this feature's catalogue keys live under. Must equal the
   * slug; `locale/<locale>/<slug>.json` is merged automatically. A feature never
   * edits another feature's catalogue and never edits a shared one.
   */
  readonly localeNamespace: FeatureSlug;
  /**
   * The chapter's screen guides and guided tours, keyed by route `path` exactly as declared in
   * `routes` (ADR 9044, `./guides.ts`). Optional — but a chapter that declares it declares it
   * for EVERY route, each a guide or an exemption with its reason: `tools/doc-sections.mjs`
   * fails `census:features` on a route with no entry, an entry for no route, a part unwritten
   * in any of the three locales, or a tour step whose anchor no element carries.
   */
  readonly guides?: GuideTable;
}

/**
 * Identity helper. Exists so a manifest is type-checked at its definition site
 * rather than at the point the registry collects it, which is where the error
 * message would be useless.
 */
export function defineFeature(manifest: FeatureManifest): FeatureManifest {
  return manifest;
}
